/**
 * NVM Theme - Multiplayer Theme Switcher v1
 *
 * Usage:
 * <script src="https://cloud.nevermind.website/embed/v1/nvm-theme.js" defer></script>
 *
 * Access after load:
 * window.nvm.theme
 */

import PartySocket from "partysocket";

import {
	getSocketHost,
	onDocumentReady,
	registerNVMFeature,
} from "../shared/browser";
import {
	isThemeColor,
	isThemeMode,
	parseServerMessage,
	THEME_COLORS,
	THEME_MODES,
	type ServerMessage,
	type ThemeColor,
	type ThemeMode,
} from "./contract";

const THEME_UI_SELECTOR = "[data-nvm-theme-ui]";
const VISITOR_COUNT_SELECTOR = "[data-nvm-visitor-count]";

type ThemeAction =
	| {
		kind: "color";
		value: ThemeColor;
	}
	| {
		kind: "mode";
		value: ThemeMode;
	};

function setThemeUiVisible(visible: boolean): void {
	try {
		document.querySelectorAll(THEME_UI_SELECTOR).forEach((element) => {
			if (element instanceof HTMLElement) {
				element.style.display = visible ? "" : "none";
			}
		});
	} catch {
		// Silent failure
	}
}

function getClosestAttributeValue(
	target: EventTarget | null,
	attributeName: string,
): string | null {
	if (!(target instanceof Element)) {
		return null;
	}

	return target.closest(`[${attributeName}]`)?.getAttribute(attributeName) ?? null;
}

function getThemeAction(target: EventTarget | null): ThemeAction | null {
	const action = getClosestAttributeValue(target, "data-nvm-theme-action");
	if (action !== null) {
		if (isThemeColor(action)) {
			return { kind: "color", value: action };
		}

		if (isThemeMode(action)) {
			return { kind: "mode", value: action };
		}
	}

	const colorTheme = getClosestAttributeValue(target, "data-nvm-theme-color");
	if (colorTheme !== null && isThemeColor(colorTheme)) {
		return { kind: "color", value: colorTheme };
	}

	const modeTheme = getClosestAttributeValue(target, "data-nvm-theme-mode");
	if (modeTheme !== null && isThemeMode(modeTheme)) {
		return { kind: "mode", value: modeTheme };
	}

	return null;
}

export interface NVMThemeClient {
	currentColorTheme: ThemeColor;
	currentModeTheme: ThemeMode;
	visitorCount: number;
	setTheme(colorTheme: ThemeColor, modeTheme: ThemeMode): void;
	disconnect(): void;
}

class NVMTheme implements NVMThemeClient {
	private socket!: PartySocket;
	public currentColorTheme: ThemeColor = "";
	public currentModeTheme: ThemeMode = "theme-light";
	public visitorCount = 0;
	private isConnected = false;

	constructor() {
		try {
			setThemeUiVisible(true);
			this.connect();
			this.setupEventListeners();
		} catch {
			this.handleFatalError();
		}
	}

	private connect(): void {
		try {
			this.socket = new PartySocket({
				host: getSocketHost(),
				room: "main",
				party: "theme",
			});

			this.socket.addEventListener("open", () => {
				this.isConnected = true;
				setThemeUiVisible(true);
			});

			this.socket.addEventListener("message", (event: MessageEvent) => {
				try {
					const message = parseServerMessage(JSON.parse(event.data as string));
					if (message) {
						this.handleMessage(message);
					}
				} catch {
					// Silent failure
				}
			});

			this.socket.addEventListener("error", () => {
				this.isConnected = false;
			});

			this.socket.addEventListener("close", (event: CloseEvent) => {
				this.isConnected = false;
				if (event.code === 1008) {
					this.handleFatalError();
				}
			});
		} catch {
			this.handleFatalError();
		}
	}

	private handleMessage(message: ServerMessage): void {
		try {
			switch (message.type) {
				case "theme-change":
					this.applyTheme(message.colorTheme, message.modeTheme);
					break;
				case "visitor-count":
					this.updateVisitorCount(message.count);
					break;
				case "error":
					break;
			}
		} catch {
			// Silent failure
		}
	}

	private handleFatalError(): void {
		this.isConnected = false;
		setThemeUiVisible(false);
	}

	private applyTheme(colorTheme: ThemeColor, modeTheme: ThemeMode): void {
		const body = document.body;

		THEME_COLORS.forEach((theme) => {
			if (theme) {
				body.classList.remove(theme);
			}
		});

		THEME_MODES.forEach((mode) => {
			body.classList.remove(mode);
		});

		if (colorTheme) {
			body.classList.add(colorTheme);
		}

		if (modeTheme) {
			body.classList.add(modeTheme);
		}

		this.currentColorTheme = colorTheme;
		this.currentModeTheme = modeTheme;

		window.dispatchEvent(
			new CustomEvent("nvmThemeChange", {
				detail: { colorTheme, modeTheme },
			}),
		);
	}

	private updateVisitorCount(count: number): void {
		this.visitorCount = count;

		document.querySelectorAll(VISITOR_COUNT_SELECTOR).forEach((element) => {
			element.textContent = count.toString();
		});

		window.dispatchEvent(
			new CustomEvent("nvmVisitorCountChange", {
				detail: { count },
			}),
		);
	}

	public setTheme(colorTheme: ThemeColor, modeTheme: ThemeMode): void {
		try {
			if (!isThemeColor(colorTheme) || !isThemeMode(modeTheme)) {
				return;
			}

			if (!this.isConnected || !this.socket) {
				return;
			}

			if (this.socket.readyState === WebSocket.OPEN) {
				this.socket.send(
					JSON.stringify({
						type: "set-theme",
						colorTheme,
						modeTheme,
					}),
				);
			}
		} catch {
			// Silent failure
		}
	}

	private setupEventListeners(): void {
		document.addEventListener("click", (event) => {
			const action = getThemeAction(event.target);
			if (!action) {
				return;
			}

			if (action.kind === "color") {
				this.setTheme(action.value, this.currentModeTheme);
				return;
			}

			this.setTheme(this.currentColorTheme, action.value);
		});
	}

	public disconnect(): void {
		if (this.socket) {
			this.socket.close();
		}
	}
}

declare global {
	interface NVMGlobalRegistry {
		theme?: NVMThemeClient;
	}
}

function initNVMTheme(): void {
	try {
		setThemeUiVisible(false);
		registerNVMFeature("theme", new NVMTheme());
	} catch {
		// Completely silent failure
	}
}

onDocumentReady(initNVMTheme);
